import React, { useCallback, useMemo } from 'react';

import {
  arrayOf, func, shape, string, bool,
} from 'prop-types';
import cn from 'classnames';
import { Product as ProductIcon, ProductVariant } from '@picsio/icons';
import { Icon } from '@picsio/ui';

import Tag from '../../../../Tag';
import Tooltip from '../../../../Tooltip';
import { getListingIconColor, MASTER_ICON_COLOR } from '../../ProductLinksSections/helpers/iconColors';
import {
  isProductLinkActionable,
  isVariantLinkActionable,
} from '../../ProductLinksSections/helpers/productLinkActionable';
import s from '../styles.module.scss';

const MAX_VISIBLE_CHIPS = 4;

const getChipKey = (chip) => {
  const linkId = chip.link?.variantId || chip.link?._id || chip.title;
  return `${chip.kind}:${linkId}:${chip.isDraft}`;
};

function AltTextUsageChips({ chips, onNavigateProduct, onNavigateVariant }) {
  const visibleChips = useMemo(
    () => chips.slice(0, MAX_VISIBLE_CHIPS),
    [chips],
  );
  const hiddenChips = useMemo(
    () => chips.slice(MAX_VISIBLE_CHIPS),
    [chips],
  );
  const hiddenCount = hiddenChips.length;

  const handleChipClick = useCallback((chip) => {
    if (chip.kind === 'variant') {
      onNavigateVariant(chip.link);
      return;
    }

    onNavigateProduct(chip.link);
  }, [onNavigateProduct, onNavigateVariant]);

  const renderUsageChip = useCallback((chip, chipClassName = s.chip) => {
    const iconColor = chip.isMaster
      ? MASTER_ICON_COLOR
      : getListingIconColor(chip.isDraft);
    const icon = chip.kind === 'variant' ? <ProductVariant /> : <ProductIcon />;
    const isClickable = chip.kind === 'variant'
      ? isVariantLinkActionable(chip.link)
      : isProductLinkActionable(chip.link, { isMaster: chip.isMaster });

    return (
      <Tag
        type="product"
        className={chipClassName}
        text={chip.title}
        icon={(
          <Icon>
            {icon}
          </Icon>
        )}
        iconColor={iconColor}
        showNumber={false}
        tooltipText={chip.tooltipText ?? chip.title}
        hideCloseBtn
        onClick={isClickable ? () => handleChipClick(chip) : undefined}
      />
    );
  }, [handleChipClick]);

  const hiddenChipsTooltip = useMemo(() => (
    <div className={s.chipCounterTooltip}>
      {hiddenChips.map((chip) => (
        <div key={getChipKey(chip)} className={s.chipCounterTooltipItem}>
          {renderUsageChip(chip, cn(s.chip, s.chipCounterTooltipChip))}
        </div>
      ))}
    </div>
  ), [hiddenChips, renderUsageChip]);

  return (
    <div className={s.chips}>
      {visibleChips.map((chip) => (
        <React.Fragment key={getChipKey(chip)}>
          {renderUsageChip(chip)}
        </React.Fragment>
      ))}
      <If condition={hiddenCount > 0}>
        <Tooltip
          content={hiddenChipsTooltip}
          placement="top"
          interactive
          delayHide={200}
        >
          <span className={s.chipCounter}>
            +{hiddenCount}
          </span>
        </Tooltip>
      </If>
    </div>
  );
}

AltTextUsageChips.propTypes = {
  chips: arrayOf(shape({
    kind: string,
    title: string,
    tooltipText: string,
    isDraft: bool,
    isMaster: bool,
    link: shape({}),
  })).isRequired,
  onNavigateProduct: func.isRequired,
  onNavigateVariant: func.isRequired,
};

export default AltTextUsageChips;
